/* eslint-disable no-console */
import { jsPDF } from 'jspdf';

import { SlideElementImageType } from '../../components/SlideComponents/slide.types';
import { Telemetry } from '../../hooks/useTelemetry';
import backendService from '../../services/backendService';
import { SVG_IMAGE_ZOOM } from './constants';

const imageToPng = async (imageUrl: string): Promise<string> => {
  const fileDimensions = await getImageDimensions(imageUrl);

  const canvas = document.createElement('canvas');
  canvas.width = fileDimensions.width * SVG_IMAGE_ZOOM;
  canvas.height = fileDimensions.height * SVG_IMAGE_ZOOM;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale_factor = Math.min(canvas.width / img.width, canvas.height / img.height);
      const newWidth = img.width * scale_factor;
      const newHeight = img.height * scale_factor;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject('Unable to create a context');
        return;
      }
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      const imageData = canvas.toDataURL();
      canvas.remove();
      img.remove();
      resolve(imageData);
    };
    img.onerror = reject;
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
  });
};

export const addImage = async (pdf: jsPDF, element: SlideElementImageType) => {
  try {
    let mediaUrl = element.mediaUrl;
    const isExternalMediaUrl = (element.mediaUrl ?? '').startsWith('http');
    if (!isExternalMediaUrl) {
      mediaUrl = await backendService.instance.gameFiles.fetchCroppedImageV2({
        fileId: element.mediaFileId ?? '',
        isOwner: true,
        isFromDiscover: false,
        isAuthenticated: true,
        cropConfig: element.mediaCropConfig,
      });
    }

    if (!mediaUrl) {
      console.error('Image could not be loaded');
      Telemetry()?.noticeError(new Error('Pdf export image url could not be loaded'));
      return;
    }

    pdf.saveGraphicsState();
    const radiusWidth = element.width * ((element.borderRadius ?? 0) / 100);
    const radiusHeight = element.height * ((element.borderRadius ?? 0) / 100);
    pdf.roundedRect(
      element.x,
      element.y,
      element.width,
      element.height,
      radiusWidth,
      radiusHeight,
      null,
    );
    pdf.clip();
    pdf.discardPath();
    const mimeSubtype = /^([^\/]+\/)?(.*)$/.exec(element.mediaMimetype);
    if (mimeSubtype && mimeSubtype[2] === 'gif') {
      const pngImage = await imageToPng(mediaUrl);
      pdf.addImage(
        pngImage,
        '',
        element.x,
        element.y,
        element.width,
        element.height,
        undefined,
        'FAST',
      );
    } else {
      pdf.addImage(
        mediaUrl,
        '',
        element.x,
        element.y,
        element.width,
        element.height,
        undefined,
        'FAST',
      );
    }
  } catch (err) {
    console.error('Image could not be added');
    console.error(err);
    Telemetry()?.noticeError(new Error('Pdf export image could not be loaded'));
  }
  pdf.restoreGraphicsState();
};

export const getImageDimensions = async (
  url: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = url;
  });
};
